import { FdeCdfSpecification } from 'features/dataLayers/generation/fdeCdfSpecification'
import EmatCombinedCategoryWorker from 'features/dataLayers/generation/workers/calculateEmatCombinedCategoryData.worker?worker'
import { useCdfRawValues2DArray } from 'features/dataLayers/load/useCdfRawValues2DArray'
import { getInspectionDate } from 'features/fde'
import { useMemo } from 'react'
import {
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoInspection,
  GeckoMeasurementUnit,
  GeckoView,
  RolodexEntryType,
} from 'types'
import { DATE_FORMAT } from 'utils/fde/constants'

// Placeholder thickness value when actual thickness data is not available
const PLACEHOLDER_THICKNESS = 1.0

/**
 * Hook that handles EMAT Combined Category CDF discovery and data extraction
 * This encapsulates all the EMAT-specific logic that was previously in the extension
 */
export function useEmatCombinedCategoryData(
  cdfSpecification: FdeCdfSpecification,
  componentDataFrames: readonly GeckoComponentDataFrame[] | undefined,
  selectedCDS: GeckoComponentDataset | undefined,
  selectedInspection?: GeckoInspection | undefined
) {
  // Find the EMAT CDFs - use a more stable selector to avoid infinite loops
  const ematCategoryCdf = useMemo(() => {
    if (
      cdfSpecification !== FdeCdfSpecification.EmatCombinedCategory ||
      !selectedCDS?.id
    )
      return undefined

    return componentDataFrames?.find((cdf) => {
      if (
        cdf.data?.params?.source?.id !== 'emat' ||
        cdf.data?.params?.view?.id !== 'emat-category'
      ) {
        return false
      }
      // Match directly by CDS ID from the component-dataset tag
      const matches =
        cdf.tags[RolodexEntryType.COMPONENT_DATASET] === selectedCDS.id
      return matches
    })
  }, [cdfSpecification, selectedCDS?.id, componentDataFrames])

  const differenceFromNominalCdf = useMemo(() => {
    if (cdfSpecification !== FdeCdfSpecification.EmatCombinedCategory) {
      return undefined
    }

    const percentageDfnCdfs = componentDataFrames?.filter((cdf) => {
      return (
        cdf.data?.params?.measurement_unit.id ===
          GeckoMeasurementUnit.Percent.id &&
        cdf.data?.params?.view?.id === GeckoView.DifferenceFromNominal.id
      )
    })

    if (percentageDfnCdfs && selectedCDS?.id) {
      return percentageDfnCdfs.find((cdf) => {
        return (
          (!selectedInspection ||
            cdf.tags[RolodexEntryType.INSPECTION] === selectedInspection.id) &&
          cdf.tags[RolodexEntryType.COMPONENT_DATASET] === selectedCDS.id
        )
      })
    }

    return undefined
  }, [
    cdfSpecification,
    selectedCDS?.id,
    componentDataFrames,
    selectedInspection,
  ])

  const { array: ematCategoryData, isLoading: ematCategoryLoading } =
    useCdfRawValues2DArray(
      cdfSpecification === FdeCdfSpecification.EmatCombinedCategory
        ? ematCategoryCdf
        : undefined
    )

  const {
    array: differenceFromNominalData,
    isLoading: differenceFromNominalLoading,
  } = useCdfRawValues2DArray(
    cdfSpecification === FdeCdfSpecification.EmatCombinedCategory
      ? differenceFromNominalCdf
      : undefined
  )

  return useMemo(
    () => ({
      ematCategoryCdf,
      differenceFromNominalCdf,
      ematCategoryData,
      differenceFromNominalData,
      ematCategoryLoading,
      differenceFromNominalLoading,
    }),
    [
      differenceFromNominalCdf,
      differenceFromNominalData,
      differenceFromNominalLoading,
      ematCategoryCdf,
      ematCategoryData,
      ematCategoryLoading,
    ]
  )
}

/**
 * Custom EMAT worker function that extracts data and calls worker directly
 */
export async function callEmatCombinedCategoryWorker(
  referenceCdf: GeckoComponentDataFrame,
  selectedInspection: GeckoInspection | undefined,
  assetModel: GeckoAssetModel | undefined,
  configToUse: ForecastComponentConfig | undefined,
  componentSlug: string | undefined,
  ematCategoryData: readonly (readonly number[])[],
  differenceFromNominalData: readonly (readonly number[])[]
): Promise<number[][]> {
  if (
    !ematCategoryData ||
    ematCategoryData.length === 0 ||
    !differenceFromNominalData ||
    differenceFromNominalData.length === 0
  ) {
    throw new Error(
      'Required EMAT data not available for combined category calculation'
    )
  }

  // We need to get the actual thickness values from the reference CDF for bin iteration
  // For EMAT combined category, we can use the EMAT data dimensions or reference CDF data
  let thicknessValues: readonly (readonly number[])[] = []

  if (referenceCdf) {
    if (ematCategoryData.length > 0) {
      thicknessValues = ematCategoryData.map((row) =>
        row.map(() => PLACEHOLDER_THICKNESS)
      )
    }
  }

  const worker = new EmatCombinedCategoryWorker()

  return new Promise((resolve, reject) => {
    worker.onmessage = (event) => {
      if (event.data?.error) {
        console.error('EMAT worker returned error:', event.data.error)
        worker.terminate()
        reject(new Error(event.data.error))
        return
      }

      if (!Array.isArray(event.data)) {
        console.error('EMAT worker returned invalid data format:', event.data)
        worker.terminate()
        reject(new Error('Worker returned invalid data format'))
        return
      }

      worker.terminate()
      resolve(event.data)
    }

    worker.onerror = (error) => {
      console.error('EMAT worker error event:', error)
      worker.terminate()
      reject(new Error('Worker encountered an error'))
    }

    const workerParams = {
      componentId: componentSlug || '',
      assetModelJson: assetModel?.raw,
      thicknessValues,
      thicknessUnit: referenceCdf.data.params.measurement_unit.id.toString(),
      binHeightJson: referenceCdf.data.binning.bin_size.height.toJSON(),
      binWidthJson: referenceCdf.data.binning.bin_size.width.toJSON(),
      yGeometryStartJson: referenceCdf.data.geometry.start.y.toJSON(),
      xGeometryStartJson: referenceCdf.data.geometry.start.x.toJSON(),
      forecastComponentConfig: configToUse ?? {},
      inspectionDateString: selectedInspection
        ? getInspectionDate({
            inspectionSlug: referenceCdf.tags.inspection,
            inspection: selectedInspection,
          })?.format(DATE_FORMAT) || ''
        : '',
      ematCategoryData,
      differenceFromNominalData,
    }

    worker.postMessage(workerParams)
  })
}
